import type { CSSProperties, FunctionComponent, ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import SearchFailedImage from '#assets/search-failed.svg';
import { getConfig } from '../../../config/envConfig';
import { TWG_CLI_LEARN_MORE_URL } from './constants';

type TwgEmptyStateVariant = 'blocked' | 'network' | 'no-sites';

const messages = defineMessages({
  blockedHeading: {
    id: 'oauth.twg.emptyState.blocked.heading',
    defaultMessage: 'Permissions are blocked for this site',
    description:
      'Heading shown when organization policy blocks TWG permissions for the selected site',
  },
  blockedDescription: {
    id: 'oauth.twg.emptyState.blocked.description',
    defaultMessage:
      'Contact your organization admin to request access or see {howPermissionsLink}',
    description:
      'Description shown when organization policy blocks TWG permissions',
  },
  howPermissionsManaged: {
    id: 'oauth.twg.emptyState.blocked.link',
    defaultMessage: 'how permissions are managed',
    description: 'Link text for documentation about permission management',
  },
  networkHeading: {
    id: 'oauth.twg.emptyState.network.heading',
    defaultMessage: 'Something went wrong',
    description:
      'Heading shown when TWG permissions could not be loaded because of a network failure',
  },
  networkDescription: {
    id: 'oauth.twg.emptyState.network.description',
    defaultMessage:
      "We're having trouble showing permissions right now. You can try refreshing the page or {supportLink}",
    description:
      'Description shown when TWG permissions could not be loaded because of a network failure',
  },
  contactSupport: {
    id: 'oauth.twg.emptyState.network.link',
    defaultMessage: 'contact support',
    description: 'Link text to Atlassian support',
  },
  noSitesHeading: {
    id: 'oauth.twg.emptyState.noSites.heading',
    defaultMessage: 'Supported sites required',
    description:
      'Heading shown when the user has no supported sites for Teamwork Graph CLI',
  },
  noSitesDescription: {
    id: 'oauth.twg.emptyState.noSites.description',
    defaultMessage:
      "Teamwork Graph CLI connects with Atlassian sites that use apps like Jira or Confluence. Your account isn't currently associated with a supported site. {learnMoreLink}",
    description:
      'Description shown when the user has no supported sites for Teamwork Graph CLI',
  },
  learnMore: {
    id: 'oauth.twg.emptyState.noSites.link',
    defaultMessage: 'Understand Teamwork Graph CLI',
    description:
      'Link text for Teamwork Graph CLI documentation when no supported sites are available',
  },
});

const styles: Record<string, CSSProperties> = {
  root: {
    alignItems: 'center',
    color: 'var(--twg-muted)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginInline: 'auto',
    maxWidth: '640px',
    minHeight: '420px',
    textAlign: 'center',
    width: '100%',
  },
  iconWrap: {
    alignItems: 'center',
    display: 'flex',
    height: '132px',
    justifyContent: 'center',
    marginBottom: '32px',
    width: '132px',
  },
  searchIcon: {
    display: 'block',
    height: '132px',
    width: '132px',
  },
  warningIcon: {
    borderBottomColor: 'var(--twg-empty-icon)',
    borderBottomStyle: 'solid',
    borderBottomWidth: '112px',
    borderLeftColor: 'transparent',
    borderLeftStyle: 'solid',
    borderLeftWidth: '64px',
    borderRightColor: 'transparent',
    borderRightStyle: 'solid',
    borderRightWidth: '64px',
    height: 0,
    position: 'relative',
    width: 0,
  },
  warningMark: {
    color: '#ffffff',
    fontSize: '56px',
    fontWeight: 500,
    left: '-8px',
    lineHeight: '56px',
    position: 'absolute',
    top: '38px',
  },
  heading: {
    color: 'var(--twg-text)',
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '32px',
    marginBlockEnd: '12px',
    marginBlockStart: 0,
  },
  description: {
    color: 'var(--twg-muted)',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    margin: 0,
  },
  link: {
    color: 'var(--twg-link)',
    fontWeight: 700,
    textDecoration: 'none',
  },
};

const WarningTriangle = () => (
  <span style={styles.iconWrap} aria-hidden="true">
    <span style={styles.warningIcon}>
      <span style={styles.warningMark}>!</span>
    </span>
  </span>
);

const StateIcon: FunctionComponent<{ variant: TwgEmptyStateVariant }> = ({
  variant,
}) => {
  if (variant === 'blocked') {
    return (
      <span style={styles.iconWrap} aria-hidden="true">
        <img src={SearchFailedImage} alt="" style={styles.searchIcon} />
      </span>
    );
  }

  return <WarningTriangle />;
};

const TwgEmptyState: FunctionComponent<{ variant: TwgEmptyStateVariant }> = ({
  variant,
}) => {
  const { formatMessage } = useIntl();
  const { support, twg } = getConfig();

  let heading: string;
  let description: ReactNode;

  if (variant === 'blocked') {
    heading = formatMessage(messages.blockedHeading);
    description = (
      <FormattedMessage
        {...messages.blockedDescription}
        values={{
          howPermissionsLink: (
            <a
              href={twg.permissionsManagementDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              {formatMessage(messages.howPermissionsManaged)}
            </a>
          ),
        }}
      />
    );
  } else if (variant === 'network') {
    heading = formatMessage(messages.networkHeading);
    description = (
      <FormattedMessage
        {...messages.networkDescription}
        values={{
          supportLink: (
            <a
              href={support.contactSupportUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              {formatMessage(messages.contactSupport)}
            </a>
          ),
        }}
      />
    );
  } else {
    heading = formatMessage(messages.noSitesHeading);
    description = (
      <FormattedMessage
        {...messages.noSitesDescription}
        values={{
          learnMoreLink: (
            <a
              href={TWG_CLI_LEARN_MORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              {formatMessage(messages.learnMore)}
            </a>
          ),
        }}
      />
    );
  }

  return (
    <div style={styles.root}>
      <StateIcon variant={variant} />
      <h2 style={styles.heading}>{heading}</h2>
      <p style={styles.description}>{description}</p>
    </div>
  );
};

export default TwgEmptyState;
